import { useMemo, useState } from "react";
import { useResignation } from "./hooks/useResignation";
import { Card } from "src/components/ui/Card";
import { AlertCircle, ChevronRight, Inbox } from "lucide-react";
import { Skeleton } from "src/components/ui/Skeleton";
import { Resignation, ResignEmployee } from "./resignation.types";
import { formatDate, getInitials } from "./utils";
import ResignationDetails, {
  StatusBadge,
} from "./components/resignation-details";
import { cn } from "src/utils";
import { Dialog, DialogContent } from "src/components/dialogue/radix-dialog";

type ResignationRow = Resignation & { employee?: { name?: string } };

const FILTERS = ["ALL", "PENDING", "APPROVED", "REJECTED"] as const;
type Filter = (typeof FILTERS)[number];

function ListSkeleton() {
  return (
    <div
      className="space-y-3 p-4"
      role="status"
      aria-label="Loading resignation requests"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-xl" />
      ))}
    </div>
  );
}

export function Avatar({
  name,
  size = "md",
}: {
  name: string;
  size?: "md" | "lg";
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary",
        size === "lg" ? "size-14 text-lg" : "size-10 text-sm",
      )}
    >
      {getInitials(name)}
    </span>
  );
}

function EmployeeCell({ employee }: { employee: ResignEmployee }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={employee.full_name} />
      <div className="min-w-0">
        <p className="truncate font-medium">{employee.full_name}</p>
        <p className="truncate text-sm text-muted-foreground">
          {employee.employee_code}
        </p>
      </div>
    </div>
  );
}

function Separation() {
  const { data, isLoading, isError } = useResignation();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const requests = useMemo(
    () =>
      [...((data as ResignationRow[] | undefined) ?? [])].sort(
        (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
      ),
    [data],
  );

  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      ALL: requests.length,
      PENDING: 0,
      APPROVED: 0,
      REJECTED: 0,
    };
    requests.forEach((r) => {
      const s = r.status.toUpperCase() as Filter;
      if (s in c) c[s] += 1;
    });
    return c;
  }, [requests]);

  const selected = requests.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="mx-auto max-w-[90%] pt-12">
      <header className="mb-6">
        <h1 className="font-mono text-3xl font-semibold tracking-tight">
          Resignation requests
        </h1>
        <p className="mt-1 text-muted-foreground">
          Select a request to see its full details.
        </p>
      </header>

      <Card className="overflow-hidden rounded-2xl shadow-none">
        {isLoading ? (
          <ListSkeleton />
        ) : isError ? (
          <div className="p-4">
            <div role="alert">
              <AlertCircle className="size-4" />
              <h3>We couldn't load resignation requests. Please try again.</h3>
            </div>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
            <Inbox
              className="size-10 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="font-medium">
              {requests.length === 0 && "No resignation requests yet"}
            </p>
            <p className="text-sm text-muted-foreground">
              {requests.length === 0 && "New requests will appear here."}
            </p>
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <table className="table">
                <thead>
                  <tr>
                    <th className="pl-5">Employee</th>
                    <th>Designation</th>
                    <th>Reason</th>
                    <th>Last working day</th>
                    <th>Status</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      className="cursor-pointer"
                    >
                      <td className="py-3 pl-5">
                        <button
                          type="button"
                          onClick={() => setSelectedId(r.id)}
                          aria-label={`View resignation details for ${r.employee.full_name}`}
                          className="w-full rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <EmployeeCell employee={r.employee} />
                        </button>
                      </td>
                      <td>
                        <p className="font-medium">{r.employee.designation}</p>
                        <p className="text-sm text-muted-foreground">
                          {r.employee.department}
                        </p>
                      </td>
                      <td>{r.reason}</td>
                      <td>{formatDate(r.last_working_day)}</td>
                      <td>
                        <StatusBadge status={r.status} />
                      </td>
                      <td>
                        <ChevronRight
                          className="size-4 text-muted-foreground"
                          aria-hidden="true"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto max-w-5xl bg-white/80 backdrop-blur-xl">
          {selected && <ResignationDetails request={selected} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Separation;
